import { useState, useRef, useEffect } from "react";
import { Bot, X, Send, Phone, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { ScrollArea } from "@/components/ui/scroll-area";
import { LocalizedLink } from "@/components/LocalizedLink";
import { useLanguage } from "@/hooks/useLanguage";
import { motion, AnimatePresence } from "framer-motion";

interface Message {
  role: "user" | "assistant";
  content: string;
}

const LegalChatbot = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState<Message[]>([]);
  const [input, setInput] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const scrollRef = useRef<HTMLDivElement>(null);
  const { t, language } = useLanguage();

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages]);

  useEffect(() => {
    // Reset conversation when language changes
    if (messages.length > 0) {
      const welcomeMessages = {
        en: "Hello! I'm here to help answer your questions about Daniel Albert Law Firm's legal services. How can I assist you today?",
        es: "¡Hola! Estoy aquí para ayudar a responder sus preguntas sobre los servicios legales del Bufete Daniel Albert. ¿Cómo puedo asistirle hoy?",
        vi: "Xin chào! Tôi ở đây để giúp trả lời các câu hỏi của bạn về dịch vụ pháp lý của Văn phòng Luật Daniel Albert. Tôi có thể giúp gì cho bạn hôm nay?"
      };
      
      setMessages([
        { role: "assistant", content: welcomeMessages[language as keyof typeof welcomeMessages] || welcomeMessages.en }
      ]);
    }
  }, [language]);

  const streamChat = async (userMessage: string) => {
    const newMessages = [...messages, { role: "user" as const, content: userMessage }];
    setMessages(newMessages);
    setIsLoading(true);

    try {
      const response = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/legal-chatbot`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}`,
        },
        body: JSON.stringify({ 
          messages: newMessages,
          language 
        }),
      });

      if (!response.ok) {
        throw new Error("Failed to get response");
      }

      const reader = response.body?.getReader();
      const decoder = new TextDecoder();
      let assistantMessage = "";

      if (!reader) throw new Error("No reader available");

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        const chunk = decoder.decode(value);
        const lines = chunk.split("\n");

        for (const line of lines) {
          if (line.startsWith("data: ")) {
            const data = line.slice(6);
            if (data === "[DONE]") continue;

            try {
              const parsed = JSON.parse(data);
              const content = parsed.choices?.[0]?.delta?.content;

              if (content) {
                assistantMessage += content;
                setMessages([
                  ...newMessages,
                  { role: "assistant", content: assistantMessage },
                ]);
              }
            } catch (e) {
              // Skip invalid JSON
            }
          }
        }
      }

      setIsLoading(false);
    } catch (error) {
      console.error("Chat error:", error);
      const errorMessages = {
        en: "I apologize, but I'm having trouble responding right now. Please call us at (832) 930-3059 for immediate assistance.",
        es: "Disculpe, pero estoy teniendo problemas para responder en este momento. Por favor llame al (832) 356-7254 para asistencia inmediata.",
        vi: "Xin lỗi, nhưng tôi đang gặp sự cố khi trả lời. Vui lòng gọi (832) 930-3059 để được hỗ trợ ngay lập tức."
      };
      
      setMessages([
        ...newMessages,
        { role: "assistant", content: errorMessages[language as keyof typeof errorMessages] || errorMessages.en },
      ]);
      setIsLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!input.trim() || isLoading) return;

    const userMessage = input.trim();
    setInput("");
    await streamChat(userMessage);
  };

  const handleOpen = () => {
    setIsOpen(true);
    if (messages.length === 0) {
      const welcomeMessages = {
        en: "Hello! I'm here to help answer your questions about Daniel Albert Law Firm's legal services. How can I assist you today?",
        es: "¡Hola! Estoy aquí para ayudar a responder sus preguntas sobre los servicios legales del Bufete Daniel Albert. ¿Cómo puedo asistirle hoy?",
        vi: "Xin chào! Tôi ở đây để giúp trả lời các câu hỏi của bạn về dịch vụ pháp lý của Văn phòng Luật Daniel Albert. Tôi có thể giúp gì cho bạn hôm nay?"
      };
      
      setMessages([
        { role: "assistant", content: welcomeMessages[language as keyof typeof welcomeMessages] || welcomeMessages.en }
      ]);
    }
  };

  return (
    <>
      {/* Chatbot Toggle Button */}
      <AnimatePresence>
        {!isOpen && (
          <motion.div
            initial={{ scale: 0, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0, opacity: 0 }}
            className="fixed bottom-6 right-6 z-50"
          >
            <Button
              onClick={handleOpen}
              size="lg"
              className="h-16 w-16 rounded-full shadow-2xl bg-blue-600 hover:bg-blue-700 text-white group"
              aria-label="Open legal assistant chatbot"
            >
              <Bot className="h-11 w-11 group-hover:scale-110 transition-transform" />
            </Button>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Chatbot Window */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, y: 100, scale: 0.8 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 100, scale: 0.8 }}
            className="fixed bottom-6 right-6 z-50 w-[380px] h-[600px] bg-card border-2 border-primary/20 rounded-2xl shadow-2xl flex flex-col overflow-hidden"
          >
            {/* Header */}
            <div className="bg-blue-600 p-4 flex items-center justify-between border-b">
              <div className="flex items-center gap-3">
                <div className="h-10 w-10 rounded-full bg-white/20 flex items-center justify-center">
                  <Bot className="h-5 w-5 text-white" />
                </div>
                <div>
                  <h3 className="font-semibold text-white">{t('chatbot.title')}</h3>
                  <p className="text-xs text-blue-100">{t('chatbot.subtitle')}</p>
                </div>
              </div>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setIsOpen(false)}
                className="h-8 w-8 p-0 text-white hover:bg-white/20"
                aria-label="Close chatbot"
              >
                <X className="h-4 w-4" />
              </Button>
            </div>

            {/* Messages */}
            <ScrollArea className="flex-1 p-4" ref={scrollRef}>
              <div className="space-y-4">
                {messages.map((message, index) => (
                  <div
                    key={index}
                    className={`flex ${message.role === "user" ? "justify-end" : "justify-start"}`}
                  >
                    <div
                      className={`max-w-[85%] rounded-2xl px-4 py-3 ${
                        message.role === "user"
                          ? "bg-blue-600 text-white"
                          : "bg-muted text-foreground border"
                      }`}
                    >
                      <p className="text-sm whitespace-pre-wrap">{message.content}</p>
                    </div>
                  </div>
                ))}
                {isLoading && (
                  <div className="flex justify-start">
                    <div className="bg-muted text-foreground rounded-2xl px-4 py-3 border">
                      <Loader2 className="h-4 w-4 animate-spin" />
                    </div>
                  </div>
                )}
              </div>
            </ScrollArea>

            {/* Quick Actions */}
            <div className="px-4 py-2 border-t bg-muted/30">
              <Button
                asChild
                size="sm"
                variant="outline"
                className="w-full text-xs"
              >
                <LocalizedLink to="/appointment" onClick={() => setIsOpen(false)}>
                  {t('chatbot.schedule')}
                </LocalizedLink>
              </Button>
            </div>

            {/* Input */}
            <form onSubmit={handleSubmit} className="p-4 border-t bg-background">
              <div className="flex gap-2">
                <Input
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  placeholder={t('chatbot.placeholder')}
                  disabled={isLoading}
                  className="flex-1"
                />
                <Button
                  type="submit"
                  size="icon"
                  disabled={!input.trim() || isLoading}
                  className="shrink-0"
                >
                  <Send className="h-4 w-4" />
                </Button>
              </div>
            </form>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default LegalChatbot;